import { useReducedMotion } from "motion/react";
import { useCallback, useLayoutEffect, useMemo, useRef, useState } from "react";

import type { NotificationData } from "../types";

const SCROLLED_THRESHOLD_PX = 48;
const AT_TOP_THRESHOLD_PX = 4;

function countPrepended(notifications: NotificationData[], previousFirstId: string | null) {
  if (previousFirstId === null) return 0;
  return Math.max(notifications.findIndex((n) => n.id === previousFirstId), 0);
}

export function useNewNotificationTracker(notifications: NotificationData[]) {
  const prefersReducedMotion = useReducedMotion();
  const scrollRef = useRef<HTMLDivElement>(null);
  const isScrolledRef = useRef(false);
  const previousFirstIdRef = useRef<string | null>(null);
  const previousScrollHeightRef = useRef(0);
  const [pendingCount, setPendingCount] = useState(0);

  const newIds = useMemo(
    () =>
      new Set(
        notifications
          .slice(0, countPrepended(notifications, previousFirstIdRef.current))
          .map((n) => n.id),
      ),
    [notifications],
  );

  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    const prepended = countPrepended(notifications, previousFirstIdRef.current);
    if (prepended > 0 && isScrolledRef.current) {
      el.scrollTop += el.scrollHeight - previousScrollHeightRef.current;
      setPendingCount((prev) => prev + prepended);
    }

    previousFirstIdRef.current = notifications[0]?.id ?? null;
    previousScrollHeightRef.current = el.scrollHeight;
  }, [notifications]);

  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    isScrolledRef.current = el.scrollTop > SCROLLED_THRESHOLD_PX;
    if (el.scrollTop <= AT_TOP_THRESHOLD_PX) setPendingCount(0);
  }, []);

  const scrollToTop = useCallback(() => {
    scrollRef.current?.scrollTo({ top: 0, behavior: prefersReducedMotion ? "auto" : "smooth" });
    setPendingCount(0);
  }, [prefersReducedMotion]);

  return { scrollRef, handleScroll, newIds, pendingCount, scrollToTop };
}
